import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { serviceTypesApi } from '@/api/service-types'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { showError } from '@/lib/error'
import type { ServiceType } from '@/types/service-record'

interface ServiceTypeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editing: ServiceType | null
  onSaved: () => void
}

export function ServiceTypeDialog({ open, onOpenChange, editing, onSaved }: ServiceTypeDialogProps) {
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<{ label: string; remark: string; is_avatar: boolean; enabled: boolean }>({
    defaultValues: { label: '', remark: '', is_avatar: false, enabled: true },
  })

  useEffect(() => {
    if (open) {
      reset({ label: editing?.label || '', remark: editing?.remark || '', is_avatar: editing?.is_avatar || false, enabled: editing?.enabled ?? true })
    }
  }, [open, editing, reset])

  const isAvatar = watch('is_avatar')
  const enabled = watch('enabled')

  const onSubmit = handleSubmit(async data => {
    try {
      if (editing) {
        await serviceTypesApi.update(editing.id, data)
        toast.success('服务类型已更新')
      } else {
        await serviceTypesApi.create(data)
        toast.success('服务类型已创建')
      }
      onOpenChange(false)
      onSaved()
    } catch (error) {
      showError(error, editing ? '更新服务类型失败' : '创建服务类型失败')
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? '编辑服务类型' : '新增服务类型'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">类型名称</label>
            <Input {...register('label', { required: '请填写类型名称', maxLength: { value: 60, message: '不能超过60字' } })} placeholder="如：电话回访" />
            {errors.label?.message && <p className="text-sm text-destructive">{errors.label.message}</p>}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">备注</label>
            <Input {...register('remark', { maxLength: { value: 255, message: '不能超过255字' } })} placeholder="选填" />
            {errors.remark?.message && <p className="text-sm text-destructive">{errors.remark.message}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="service-type-is-avatar"
              checked={isAvatar}
              onCheckedChange={v => setValue('is_avatar', v === true)}
            />
            <label htmlFor="service-type-is-avatar" className="text-sm">该类型服务要求上传图片</label>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">状态</label>
            <Select value={enabled ? 'enabled' : 'disabled'} onValueChange={v => setValue('enabled', v === 'enabled')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="enabled">启用</SelectItem>
                <SelectItem value="disabled">停用</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">停用后录入不可选、服务统计不再展示该类型</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            <Button type="submit">保存</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
